/*global SlimCD*/
/*eslint no-undef: "error"*/
import React, { useState } from "react";
import { enUS, enAU } from "date-fns/locale";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useTheme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import withStyles from "@mui/styles/withStyles";
import { green } from "@mui/material/colors";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Paper from "@mui/material/Paper";
import useMediaQuery from "@mui/material/useMediaQuery";
import InfoIcon from "@mui/icons-material/Info";

import { TimeLoader } from "@/_components";
import { importScript } from "@/_helpers";

import { logGeneralException } from "../_api/generalExceptionApi";
import { securePayment } from "../_redux/actions/paymentActions";

const CalculatingTypography = withStyles({
  root: {
    fontSize: 14,
    color: "#1c355e",
    fontWeight: 600,
    paddingTop: 2,
    paddingLeft: 10,
  },
})(Typography);

const GreenCheckbox = withStyles({
  root: {
    color: green[400],
    "&$checked": {
      color: green[600],
    },
    transform: "scale(0.8)",
    paddingLeft: 0,
    paddingRight: 0,
  },
  checked: {},
})((props) => <Checkbox color="default" {...props} />);

const RedTextTypography = withStyles({
  root: {
    textAlign: "center",
    fontSize: 16,
    color: "#d1495b",
    fontWeight: 600,
    fontStyle: "italic",
  },
})(Typography);

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    [theme.breakpoints.up("sm")]: { padding: 15 },
    [theme.breakpoints.down("sm")]: {
      paddingTop: theme.spacing(4),
      marginTop: 10,
    },
    borderRadius: 4,
    border: "1px solid rgba(0, 0, 0, .125)",
    marginTop: 50,
  },
  titleTypo: {
    fontWeight: 600,
    [theme.breakpoints.up("sm")]: { fontSize: 16 },
    [theme.breakpoints.down("sm")]: {
      fontSize: 10,
      marginTop: 10,
      marginBottom: 10,
    },
    textAlign: "center",
  },
  headerGrid: {
    [theme.breakpoints.down("sm")]: {
      padding: "0 10px",
      marginTop: 10,
      marginBottom: 10,
    },
    [theme.breakpoints.up("sm")]: { padding: "10px 0" },
    justifyContent: "center",
  },
  creditCardHolder: {
    width: 50,
    height: 32,
    border: "1px solid #1c355e",
    borderRadius: 4,
    backgroundColor: "#fff",
    boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
  },
  creditCard: {
    paddingTop: 2,
    paddingLeft: 2,
    width: 46,
    height: 28,
  },
  cardHolder: {
    height: 190,
    width: 320,
    borderRadius: 10,
    background: "url(./src/_images/CreditCards/CreditCard_Blue.png)",
    backgroundSize: "320px 190px",
    backgroundRepeat: "no-repeat",
    boxShadow: "0 4px 10px rgba(0,0,0,0.3)",
  },
  creditCardChip: {
    marginLeft: 24,
    height: 36,
    width: 48,
  },
  cvv2Tooltip: {
    width: 168,
    height: 130,
  },
  labelRoot: {
    "&$labelFocused": {
      fontWeight: 700,
    },
  },
  agree: {
    fontSize: 10,
    [theme.breakpoints.up("sm")]: { padding: 0 },
  },
  summaryHeader: {
    variant: "h1",
    fontWeight: 600,
    color: "#00175a",
    display: "flex",
    justifyContent: "center",
  },
  entireDialogPaper: {
    borderRadius: 0,
  },
  dialogPaper: {
    padding: theme.spacing(2),
    textAlign: "center",
    width: 340,
    borderRadius: 0,
  },
  divideMe: {
    marginTop: 10,
    marginBottom: 10,
  },
  labelFocused: {},
}));

function SecurePaymentMethod({
  validateSecurePaymentData,
  securePayment,
  setIsSuccess,
}) {
  const classes = useStyles();
  const [selectedDate, setSelectedDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [checked, setChecked] = useState(false);
  const [errors, setErrors] = useState({});
  const { token } = useParams();
  const theme = useTheme();
  const isXtraSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const isXLSmall = useMediaQuery(theme.breakpoints.down("xl"));
  const { t } = useTranslation();
  importScript("https://trans.slimcd.com/soft/json/slimcd.js");
  const countryCode = sessionStorage.getItem("LSCountryCode");
  const locale = countryCode === "USA" ? enUS : enAU;

  function SubmitHandler() {
    event.preventDefault(); //This prevents any rerendering (postback)
    setErrors(null);
    //Get the date input value
    let formattedDate = new Date(selectedDate);
    let expmonth = (formattedDate.getMonth() + 1).toString().padStart(2, "0");
    let expyear = formattedDate.getFullYear();
    if (expyear < 2020) expyear = null;
    const identifiedErrors = {};

    // CHECK THE USER INPUT FOR VALIDITY
    if (
      SlimCD.Validate.isValidCardNumber(
        document.getElementById("cardnumber").value
      ) === false
    )
      identifiedErrors.cardnumber = true;

    if (SlimCD.Validate.isValidExpDate(expmonth, expyear) === false)
      identifiedErrors.expire = true;

    if (
      SlimCD.Validate.isValidCVV2(document.getElementById("cvv2").value) ===
      false
    )
      identifiedErrors.cvv2 = true;

    if (
      SlimCD.Validate.isValidZipCode(document.getElementById("zip").value) ===
      false
    )
      identifiedErrors.zip = true;

    setErrors(identifiedErrors);
    if (Object.keys(identifiedErrors).length === 0) {
      setSubmitting(true);
      // SUBMIT THE USER INPUT TO SLIMCD USING A LOAD TRANSACTION TYPE
      SlimCD.Transact.ProcessTransaction(
        {
          username: validateSecurePaymentData.cC_APIKey,
          amount: validateSecurePaymentData.amountDue,
          transtype: "SALE",
          cardnumber: document.getElementById("cardnumber").value,
          expmonth: expmonth,
          expyear: expyear,
          cvv2: document.getElementById("cvv2").value,
          zip: document.getElementById("zip").value,
          phone: validateSecurePaymentData.textPhoneNumber,
          send_sms: "yes",
          allow_duplicates: "yes",
        },
        function (reply) {
          // Approved or Declined a GateID will be returned
          // SAVE THE UNIQUE RETURNED GATEID AS THE TRANSACTION TOKEN IN A HIDDEN FIELD
          if (
            reply?.datablock !== undefined &&
            reply?.datablock.gateid !== undefined
          )
            document.getElementById("gateid").value = reply.datablock.gateid;

          if (reply.response == "Success") {
            // CLEAR THE CARD NUMBER AND CVV2 FIELDS TO STAY OUT OF SCOPE
            document.getElementById("cardnumber").value = "";
            document.getElementById("cvv2").value = "";

            // SAVE THE RETURNED NON-SENSATIVE CARD INFORMATION
            document.getElementById("last4").value = reply.datablock.last4;
            document.getElementById("cardtype").value =
              reply.datablock.cardtype;

            toast.success(t("securepay.successPayment"));
            setSubmitting(false);

            const requestData = {
              securePaymentID: validateSecurePaymentData.securePaymentID,
              authCode: reply.datablock.authcode,
              approvedAmount: reply.datablock.approvedamt,
              paidDate: new Date().toISOString(),
              token: token,
            };

            securePayment(requestData)
              .catch(() => {})
              .finally(() => {
                setIsSuccess(true);
              });
          } else {
            // ANNOUNCE THE PROBLEM
            toast.error(t("securepay.paymentFailure"));
            // Log exception to api
            logGeneralException({
              portalId: 2,
              description:
                t("securepay.securePaymentSaveFailure") + reply.description,
              source: "Client Portal Front",
              fileName: "SecurePay.jsx",
              functionName: "SlimCD.Transact.ProcessTransaction",
              notes: t("securepay.errorWithCreditCard"),
              addBy: 42715,
              clientID: validateSecurePaymentData.clientID,
            });
            setSubmitting(false);
          }
        }
      );
    }
  }

  const handleCheckChange = (event) => {
    setChecked(event.target.checked);
  };

  return (
    <>
      <Dialog
        open={submitting}
        onClose={() => setSubmitting(false)}
        aria-labelledby="calculating-overlay"
        classes={{ paper: classes.entireDialogPaper }}
      >
        <Grid container direction="column">
          <Grid item>
            <Paper className={classes.dialogPaper} elevation={0}>
              <Grid container direction="column">
                <Grid item>
                  <CalculatingTypography>
                    {t("payments.calculatingPayment")}
                  </CalculatingTypography>
                </Grid>
                <Divider className={classes.divideMe} />
                <Grid
                  item
                  style={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <TimeLoader />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Dialog>

      <div className={classes.root}>
        <Grid
          container
          direction="column"
          alignItems={isXtraSmall ? "center" : null}
        >
          <Typography
            variant="h6"
            gutterBottom
            className={classes.summaryHeader}
            sx={{ fontSize: isXtraSmall ? "16px" : "20px" }}
          >
            {t("payments.paymentMethod")}
          </Typography>
          <Grid item style={{ marginTop: 20 }}>
            <form
              //method="post"
              id="verifyCardForm"
              onSubmit={() => SubmitHandler(this)}
            >
              {/* Items returned from SlimCD */}
              <input type="hidden" name="gateid" id="gateid" value="" />
              <input type="hidden" name="last4" id="last4" value="" />
              <input type="hidden" name="cardtype" id="cardtype" value="" />

              <Grid container>
                <Grid
                  item
                  xs={12}
                  xl={7}
                  style={{
                    display: isXLSmall ? "flex" : "",
                    justifyContent: isXLSmall ? "center" : "",
                  }}
                >
                  <div className={classes.cardHolder}>
                    <Grid container direction="row" alignItems="center">
                      <Grid item xs={4} style={{ marginTop: 6 }}>
                        <img
                          className={classes.creditCardChip}
                          src={"./src/_images/CreditCards/CreditCard_Chip.png"}
                        />
                      </Grid>
                      <Grid
                        item
                        xs={8}
                        style={{ textAlign: "right", paddingRight: 20 }}
                      >
                        <TextField
                          onInput={(e) => {
                            e.target.value = e.target.value.replace(
                              /[^0-9]/g,
                              ""
                            );
                          }}
                          variant="outlined"
                          margin="dense"
                          size="small"
                          id="zip"
                          label={t("payments.zipCode")}
                          autoFocus
                          //required
                          disabled={submitting}
                          error={errors.zip}
                          inputProps={{
                            maxLength: 5,
                            minLength: 5,
                            autoComplete: "password",
                          }}
                          style={{
                            backgroundColor: "rgba(255,255,255,0.7)",
                            width: 120,
                          }}
                          InputLabelProps={{
                            classes: {
                              root: classes.labelRoot,
                              focused: classes.labelFocused,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                    <Grid item style={{ marginTop: 10 }}>
                      <Grid container direction="row" justifyContent="center">
                        <Grid item>
                          <TextField
                            onInput={(e) => {
                              e.target.value = e.target.value.replace(
                                /[^0-9]/g,
                                ""
                              );
                            }}
                            variant="outlined"
                            margin="dense"
                            size="small"
                            id="cardnumber"
                            label={t("payments.cardNumber")}
                            //required
                            disabled={submitting}
                            error={errors.cardnumber}
                            inputProps={{
                              maxLength: 16,
                              minLength: 15,
                              autoComplete: "password",
                            }}
                            style={{
                              backgroundColor: "rgba(255,255,255,0.7)",
                              width: 280,
                            }}
                            InputLabelProps={{
                              classes: {
                                root: classes.labelRoot,
                                focused: classes.labelFocused,
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item style={{ marginTop: 10 }}>
                      <Grid container direction="row">
                        <Grid item xs={3} style={{ paddingLeft: 20 }}>
                          <TextField
                            onInput={(e) => {
                              e.target.value = e.target.value.replace(
                                /[^0-9]/g,
                                ""
                              );
                            }}
                            variant="outlined"
                            margin="dense"
                            size="small"
                            id="cvv2"
                            label="CVV2"
                            //required
                            disabled={submitting}
                            error={errors.cvv2}
                            inputProps={{
                              maxLength: 4,
                              minLength: 3,
                              autoComplete: "password",
                            }}
                            style={{
                              backgroundColor: "rgba(255,255,255,0.7)",
                              width: 80,
                            }}
                            InputLabelProps={{
                              classes: {
                                root: classes.labelRoot,
                                focused: classes.labelFocused,
                              },
                            }}
                          />
                        </Grid>
                        <Grid item xs={1}>
                          <div style={{ marginLeft: 4, marginTop: -14 }}>
                            <Tooltip
                              placement="top"
                              disableFocusListener
                              title={
                                <>
                                  <img
                                    className={classes.cvv2Tooltip}
                                    src={
                                      "./src/_images/CreditCards/CreditCard_SecCode.png"
                                    }
                                  />
                                </>
                              }
                            >
                              <IconButton aria-label="info" size="large">
                                <InfoIcon />
                              </IconButton>
                            </Tooltip>
                          </div>
                        </Grid>
                        <Grid
                          item
                          xs={8}
                          style={{ textAlign: "right", paddingRight: 20 }}
                        >
                          <LocalizationProvider
                            dateAdapter={AdapterDateFns}
                            adapterLocale={locale}
                          >
                            <DesktopDatePicker
                              name="expire"
                              id="expire"
                              label={t("payments.expDate")}
                              inputFormat="MM/yyyy"
                              disablePast
                              openTo="year"
                              views={["year", "month"]}
                              disabled={submitting}
                              value={selectedDate}
                              onChange={setSelectedDate}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  fullWidth
                                  required
                                  margin="normal"
                                  size="small"
                                  error={errors.expire}
                                  InputLabelProps={{ shrink: true }}
                                  style={{
                                    backgroundColor: "rgba(255,255,255,0.7)",
                                    width: 170,
                                    marginTop: 9,
                                  }}
                                />
                              )}
                            />
                          </LocalizationProvider>
                        </Grid>
                      </Grid>
                    </Grid>
                  </div>
                </Grid>
                <Grid item xs={12} xl={5}>
                  <Grid item>
                    <Typography className={classes.titleTypo}>
                      {t("payments.acceptedPayMethods")}:
                    </Typography>
                  </Grid>
                  <Grid
                    container
                    direction="row"
                    alignItems="center"
                    className={classes.headerGrid}
                  >
                    <Grid item style={{ paddingRight: 10 }}>
                      <div className={classes.creditCardHolder}>
                        <img
                          className={classes.creditCard}
                          src={"./src/_images/CreditCards/Mastercard_Logo.png"}
                        />
                      </div>
                    </Grid>
                    <Grid item style={{ paddingRight: 10 }}>
                      <div className={classes.creditCardHolder}>
                        <img
                          className={classes.creditCard}
                          src={"./src/_images/CreditCards/Visa_Logo.png"}
                        />
                      </div>
                    </Grid>
                    <Grid item style={{ paddingRight: 10 }}>
                      <div className={classes.creditCardHolder}>
                        <img
                          className={classes.creditCard}
                          src={"./src/_images/CreditCards/Discover_Logo.png"}
                        />
                      </div>
                    </Grid>
                  </Grid>
                  <Grid container direction="column">
                    <Grid
                      item
                      style={{
                        border: "2px solid black",
                        borderRadius: 5,
                        textAlign: "center",
                        margin: isXtraSmall ? "0 10px" : "",
                      }}
                    >
                      <Typography style={{ fontSize: 10 }}>
                        {t("securepay.payMessage")}
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Grid
                        container
                        direction="row"
                        justifyContent="center"
                        alignItems="center"
                      >
                        <Grid item>
                          <GreenCheckbox
                            checked={checked}
                            onChange={handleCheckChange}
                            //   disabled={user.autoPayEnabled === false}
                          />
                        </Grid>
                        <Grid item>
                          <Typography
                            style={{ fontSize: 10 }}
                            className={classes.agree}
                          >
                            {t("securepay.agree")}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid
                  container
                  direction="column"
                  justifyContent="center"
                  alignItems="center"
                  style={{ width: "100%", paddingBottom: 10, paddingTop: 10 }}
                >
                  {errors.zip === true ? (
                    <Grid item>
                      <RedTextTypography>
                        {t("payments.zipCodeError")}
                      </RedTextTypography>
                    </Grid>
                  ) : null}
                  {errors.cardnumber === true ? (
                    <Grid item>
                      <RedTextTypography>
                        {t("payments.cardNumberError")}
                      </RedTextTypography>
                    </Grid>
                  ) : null}
                  {errors.cvv2 === true ? (
                    <Grid item>
                      <RedTextTypography>
                        {t("payments.cvv2Error")}
                      </RedTextTypography>
                    </Grid>
                  ) : null}
                  {errors.expire === true ? (
                    <Grid item>
                      <RedTextTypography>
                        {t("payments.expDateError")}
                      </RedTextTypography>
                    </Grid>
                  ) : null}
                </Grid>
                <Grid
                  container
                  direction="row"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Grid item>
                    <Button
                      type="submit"
                      size="medium"
                      variant="contained"
                      color="primary"
                      disabled={!checked}
                      className={classes.submit}
                      style={{
                        width: 200,
                        height: 50,
                        marginTop: isXLSmall ? 0 : 20,
                        marginBottom: isXtraSmall ? 20 : 0,
                      }}
                    >
                      {t("securepay.makePayment")}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </form>
          </Grid>
        </Grid>
      </div>
    </>
  );
}

SecurePaymentMethod.propTypes = {
  validateSecurePaymentData: PropTypes.object.isRequired,
  securePayment: PropTypes.func.isRequired,
  setIsSuccess: PropTypes.func.isRequired,
};

const mapDispatchToProps = {
  securePayment,
};

export default connect(null, mapDispatchToProps)(SecurePaymentMethod);
